/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { providers } from "dc-core";
import { publicApiNames } from "publicApis/constants";
import { apiFactory } from "api/external/ApiFactory";
import { getUIConfigApis } from "publicApis/UIConfig";
import { getFeatureConfigApis } from "publicApis/featureConfig";
import { getFeatureFlagApis } from "publicApis/featureFlag";
import { getLocalFontPrimingDialogApis } from "publicApis/localFontPrimingDialog";
import { getfeatureLifecycleHookApis } from "publicApis/featureLifecycleHook";
import { getPopupApis } from "publicApis/Popup";
import { getHCColorPallete } from "../selectors/highContrastSelectors";
import { fetchExpandedPanelComponent } from "utils/BottomSheetUtils";
import {
  getShouldUseMobileModernViewer,
  getPageThemeColorPallette,
  getSwitchPreviewState,
  getIsMultiDocModeEnabled
} from "selectors/appSelectors";
import { AppController } from "app/AppController";
import { SWITCH_PREVIEW_STATE } from "constants/enums";
import { SWITCH_PREVIEW_ANIMATION_DURATION } from "constants/constants";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import { getRHPApis } from "./RHP";

const getAppApis = stores => {
  const UIConfigApis = getUIConfigApis(stores);
  const featureFlagApis = getFeatureFlagApis();
  const localFontPrimingDialogApis = getLocalFontPrimingDialogApis();
  const featureConfigApis = getFeatureConfigApis(stores);
  const featureLifecycleHookApis = getfeatureLifecycleHookApis(stores);
  const popupApis = getPopupApis(stores);

  /**
   * This method should be used if the client wants to delegate updation
   * responsibility to preview dropin (rather than explicitly calling the above APIs)
   *
   * @param {Object} appConfig - Updated (and stringified) app config
   */
  function updateAppConfig(appConfig) {
    try {
      const parsedAppConfig = JSON.parse(appConfig);
      const { featureConfig } = parsedAppConfig;
      for (const featureId in featureConfig) {
        const shouldEnable = featureConfig[featureId];
        featureConfigApis.enableFeature(featureId, shouldEnable, parsedAppConfig);
      }
    } catch (ex) {
      console.error(ex);
    }
  }

  const getColorPalette = () => getHCColorPallete(stores);

  const getDarkThemeColorPallette = () => getPageThemeColorPallette(stores);

  const getMultiDocMode = () => getIsMultiDocModeEnabled(stores);

  function registerUserPrefThemeCallback(callback) {
    stores.pageThemeSwitcherStore.setUserPrefThemeCallback(callback);
  }

  function getSideKickContainer() {
    const isMobileMV = getShouldUseMobileModernViewer(stores);
    if (isMobileMV) {
      return Promise.resolve({
        openPanel: (verbId, opts = {}) => {
          const expandedPanelPromise = fetchExpandedPanelComponent(verbId, opts);
          const {
            useBackDrop = true,
            snapPoints = "auto",
            title,
            secondaryComponent,
            topMargin,
            containerStyles,
            headerStyles,
            currentHeight
          } = opts;
          return expandedPanelPromise.then(comp => {
            providers["tools-board-provider"]().then(toolsBoardProvider => {
              toolsBoardProvider.setConfig({
                id: verbId,
                megaVerb: verbId,
                useBackDrop,
                body: comp,
                snapPoints,
                topMargin,
                header: {
                  title,
                  secondaryComponent,
                  styles: headerStyles
                },
                containerStyles,
                currentHeight
              });
            });
          });
        },
        closePanel: () =>
          providers["tools-board-provider"]().then(toolsBoardProvider => {
            toolsBoardProvider.reset();
          }),
        subscribe: callback =>
          providers["tools-board-provider"]().then(toolsBoardProvider => {
            toolsBoardProvider.subscribe("preview", callback);
          }),
        unsubscribe: () =>
          providers["tools-board-provider"]().then(toolsBoardProvider => {
            toolsBoardProvider.unsubscribe("preview");
          }),
        beforeOpenPanel: (callback, opts) =>
          providers["tools-board-provider"]().then(toolsBoardProvider => {
            toolsBoardProvider.beforeToolActivated(callback, opts);
          })
      });
    }
    const rhpRef = stores.appStore.rhpRefValue;
    if (rhpRef) {
      return Promise.resolve(getRHPApis(stores));
    }
  }

  function switchPreview(callback) {
    return new Promise(resolve => {
      stores.appStore.setSwitchPreviewState(SWITCH_PREVIEW_STATE.PRE_SWITCH);
      setTimeout(async () => {
        stores.appStore.setSwitchPreviewState(SWITCH_PREVIEW_STATE.SWITCHING);

        stores.appStore.appController.cleanup(true);
        await callback();
        const newController = new AppController(stores, stores.appStore.appController.props);
        stores.appStore.appController = newController;
        newController.init(true);
        stores.bookmarkStore.initializeAction();
        stores.digSigStore.initializeAction();
        stores.lhpStore.attachmentsModel.initializeAction();
        stores.appStore.setSwitchPreviewState(SWITCH_PREVIEW_STATE.SWITCHED);
        subscribeToSelector(stores, getSwitchPreviewState, state => {
          if (state === SWITCH_PREVIEW_STATE.POST_SWITCH) resolve();
        });
      }, SWITCH_PREVIEW_ANIMATION_DURATION);
    });
  }

  return Object.assign({}, UIConfigApis, featureConfigApis, featureLifecycleHookApis, featureFlagApis, popupApis, {
    updateAppConfig,
    getColorPalette,
    getSideKickContainer,
    switchPreview,
    getDarkThemeColorPallette,
    registerUserPrefThemeCallback,
    localFontPrimingDialogApis,
    getMultiDocMode
  });
};

export const registerAppApis = stores => {
  apiFactory.registerApi(publicApiNames.APP, clientName => getAppApis(stores), true);
};
